<template>
  <f7-page>
    <!-- Preloader mientras carga los datos, pero que no bloquea la app como el de f7 preloader show -->
    <!-- <div v-if="!loaded" class="preloader-center">
      <f7-preloader class="margin-right"></f7-preloader>
      {{ $t('loading') }}
    </div> -->
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-if="loaded" id="main-dashboard">
      <div class="dashboard-toolbar">
        <button
          type="button"
          class="dashboard-toolbar__customize-button dashboard-toolbar__customize-button--with-tooltip"
          :aria-label="$t('dashboardCustomization.title')"
          :title="$t('dashboardCustomization.title')"
          :data-tooltip="$t('dashboardCustomization.title')"
          @click="openCustomizationDrawer"
        >
          <i class="f7-icons" aria-hidden="true">
            slider_horizontal_3
          </i>
        </button>
      </div>

      <div
        id="main-dashboard-samples"
        class="dashboard-grid"
        :class="{
          'dashboard-grid--empty': !visibleDashboardWidgets.length,
        }"
      >
        <div
          v-if="!visibleDashboardWidgets.length"
          class="dashboard-empty-widgets-state"
          role="status"
        >
          <div
            class="dashboard-empty-widgets-icon"
            aria-hidden="true"
          >
            <i class="f7-icons">slider_horizontal_3</i>
          </div>
          <div class="dashboard-empty-widgets-copy">
            <h2>No tienes widgets visibles</h2>
            <p>Abre la personalización para activar alguno.</p>
          </div>
        </div>
        <template v-else>
          <!-- El renderizado de los widgets del panel de control se realiza mediante el componente DashboardWidgetRenderer, que recibe las propiedades necesarias para mostrar cada widget según su configuración y estado, y se organiza en una cuadrícula adaptable que se ajusta al número de widgets visibles y al tamaño de la pantalla. -->
          <DashboardWidgetRenderer
            v-for="widget in visibleDashboardWidgets"
            :key="widget.id"
            class="dashboard-grid__item"
            :class="[
              `dashboard-grid__item--${getDashboardWidgetSize(widget)}`, // Cada widget se asigna a una clase CSS específica que determina su tamaño en la cuadrícula del dashboard, utilizando la función getDashboardWidgetSize para calcular el tamaño final basado en su configuración y el contexto del dashboard.
              `dashboard-grid__item--${widget.id}`,
              {
                'dashboard-grid__item--order-hover':
                  hoveredDashboardWidgetId === widget.id,
              },
            ]"
            :widget="widget"
            :widget-size="getDashboardWidgetSize(widget)"
            :aggregate-info="aggregateInfo"
            :analytics-shortcuts="analyticsShortcuts"
            :online-samples-record="onlineSamplesRecord"
            :has-online-samples="hasOnlineSamples"
            :online-samples-icon-svg="onlineSamplesIconSvg"
            :science-icon-svg="scienceIconSvg"
            :can-register-complaint="canRegisterComplaint"
            :can-register-samples="canRegisterSamples"
            :last-dashboard-refresh-at="lastDashboardRefreshAt"
            :visible-widgets-count="visibleDashboardWidgets.length"
          />
        </template>
      </div>

      <CustomizeDashboardDrawer
        v-if="isCustomizationDrawerOpen"
        :widgets="dashboardWidgets"
        @close="closeCustomizationDrawer"
        @toggle-widget="handleToggleDashboardWidget"
        @move-widget-up="handleMoveDashboardWidgetUp"
        @move-widget-down="handleMoveDashboardWidgetDown"
        @restore-default="handleRestoreDefaultDashboardLayout"
        @preview-widget-hover="handleDashboardWidgetPreviewHover"
        @preview-widget-leave="handleDashboardWidgetPreviewLeave"
      />
    </div>
    <newsInfoPopUp v-if="speaksSpanish && showNewsInfoPopUp" />
    <tutorialPopUp />
  </f7-page>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import EventBus from '@/services/event-bus';
import CustomizeDashboardDrawer from '@/components/Dashboard/CustomizeDashboardDrawer/index.vue';
import DashboardWidgetRenderer from '@/components/Dashboard/DashboardWidgetRenderer/index.vue';
import { DASHBOARD_WIDGETS } from '@/components/Dashboard/widgets/dashboardWidgetRegistry';
// Iconos SVG
import onlineSamplesIconSvg from '@/static/icons/registro_online.svg';
import scienceIconSvg from '@/static/icons/erlenmeyer-flask--science-experiment-lab-flask-chemistry-solution.svg';
import tutorialPopUp from '@/components/UtilityComponents/TutorialPopUp/tutorialPopUp.vue';
import newsInfoPopUp from '@/components/UtilityComponents/NewsInfoPopUp/newsInfoPopUp.vue';
import {
  ISSUED_INVOICES_GUIDED_TOUR_AUTO_START_DELAY,
  SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY,
} from '@/js/constants';
import {
  shouldAutoStartSamplesRegisterTemplatesGuidedTour,
  startSamplesRegisterTemplatesDashboardIntroTour,
  stopSamplesRegisterGuidedTour,
} from '@/services/samplesRegisterGuidedTour';
import {
  shouldAutoStartIssuedInvoicesGuidedTour,
  startIssuedInvoicesDashboardIntroTour,
  stopIssuedInvoicesGuidedTour,
} from '@/services/issuedInvoicesGuidedTour';
import {
  hasComplaintManagementAccess,
  hasComplaintRegisterAccess,
  hasMySmartAnalyticsAccess,
  hasNutrivisorAccess,
} from '@/js/routeAccess';

const FLEXIBLE_DASHBOARD_WIDGET_IDS = [
  'powerbi-analytics',
  'complaints',
];

export default {
  name: 'Dashboard',
  components: {
    CustomizeDashboardDrawer,
    DashboardWidgetRenderer,
    tutorialPopUp,
    newsInfoPopUp,
  },
  data() {
    return {
      loaded: false,
      materialRequest: {
        NumEnTransito: 0,
        NumSolicitudesPendientes: 0,
        NumEnPreparacion: 0,
        NumAceptadas: 0,
        NumRechazadas: 0,
      },
      onlineSamplesRecord: {
        NumSolicitudesAbiertas: 0,
        NumEnTransito: 0,
        NumRecepcionadas: 0,
      },
      guidedTourStartTimeoutId: null,
      hasTriedAutoStartSamplesGuidedTour: false,
      issuedInvoicesGuidedTourStartTimeoutId: null,
      hasTriedAutoStartIssuedInvoicesGuidedTour: false,
      isCustomizationDrawerOpen: false,
      hoveredDashboardWidgetId: null,
      lastDashboardRefreshAt: null,
      windowWidth:
        typeof window !== 'undefined' ? window.innerWidth : 1440,
    };
  },
  computed: {
    isTemplatesVideoPeriodActive() {
      return (
        String(this.currentVideo?.name || '')
          .trim()
          .toLowerCase() === 'plantillas muestreo'
      );
    },
    shortcutsCount() {
      return this.analyticsShortcuts.length;
    },
    canAccessNutrivisor() {
      return hasNutrivisorAccess({
        hasAccessToAdvanceService: this.hasAccessToAdvanceService,
        hasAccessToNutritionalMonitoring:
          this.hasAccessToNutritionalMonitoring,
      });
    },
    canAccessMySmartAnalytics() {
      return hasMySmartAnalyticsAccess(
        this.availableCountries,
        this.company
      );
    },
    usesMosaicDashboard() {
      return this.shortcutsCount > 1 && this.windowWidth >= 1280;
    },
    isCompactDashboard() {
      return this.shortcutsCount > 1 && !this.usesMosaicDashboard;
    },
    onlineSamplesLayoutMode() {
      return this.usesMosaicDashboard ? 'stacked' : 'grid';
    },
    emptyStateLayoutMode() {
      return this.usesMosaicDashboard ? 'stacked' : 'wide';
    },
    dashboardLayoutClass() {
      // El layout se adapta al número de accesos rápidos visibles para
      // evitar huecos cuando solo aparece uno o ninguno.
      return {
        'has-one-shortcut': this.shortcutsCount === 1,
        'has-two-shortcuts': this.shortcutsCount > 1,
        'has-no-shortcuts': this.shortcutsCount === 0,
        'has-samples': this.hasOnlineSamples,
        'is-compact': this.isCompactDashboard,
      };
    },
    analyticsShortcuts() {
      const shortcuts = [
        {
          title: this.$t('nutrivisor'),
          description: this.$t(
            this.hasOneVisibleSampleType
              ? 'nutrivisorCompactDescription'
              : 'nutrivisorDescription'
          ),
          routeName: 'Nutrivisor',
          variant: 'green',
        },
        {
          title: this.$t('mySmartAnalytics'),
          description: this.$t(
            this.hasOneVisibleSampleType
              ? 'mySmartAnalyticsCompactDescription'
              : 'mySmartAnalyticsDescription'
          ),
          routeName: 'PesticideResidues',
          variant: 'yellow',
        },
      ];

      // El dashboard usa exactamente la misma validación compartida que
      // menú y control de rutas para que el cambio de cliente sea coherente.
      const { canAccessNutrivisor } = this;

      // Filtrar My Smart Analytics: requiere país disponible
      // My Smart Analytics depende del prefijo de compañía habilitado en
      // availableCountries, igual que el resto de puntos de entrada.
      const { canAccessMySmartAnalytics } = this;

      return shortcuts.filter((shortcut) => {
        if (shortcut.routeName === 'Nutrivisor') {
          return canAccessNutrivisor;
        }
        if (shortcut.routeName === 'PesticideResidues') {
          return canAccessMySmartAnalytics;
        }
        return true;
      });
    },
    canShowComplaintWidget() {
      return hasComplaintManagementAccess({
        enabled: this.enabled,
        authenticatedUser: this.authenticatedUser,
        company: this.company,
      });
    },
    canRegisterComplaint() {
      return hasComplaintRegisterAccess({
        enabled: this.enabled,
        authenticatedUser: this.authenticatedUser,
        company: this.company,
      });
    },
    canRegisterSamples() {
      return Boolean(this.company);
    },
    dashboardStorageContext() {
      return {
        userId:
          this.authenticatedUser?.id ||
          this.authenticatedUser?.email ||
          this.authenticatedUser?.username,
        companyCode: this.company,
      };
    },
    dashboardAvailabilityContext() {
      return {
        company: this.company,
        hasAnySamples: this.hasAnySamples,
        permissions: {
          canAccessNutrivisor: this.canAccessNutrivisor,
          canAccessMySmartAnalytics: this.canAccessMySmartAnalytics,
          canAccessSampleRecords: true,
          canAccessComplaints: this.canShowComplaintWidget,
        },
      };
    },
    // Estas propiedades filtran los widgets en función de su estado de disponibilidad y habilitación, clasificándolos en activos, ocultos y no disponibles para su visualización en la interfaz de personalización del panel de control.
    dashboardLayoutById() {
      return this.dashboardLayout.reduce((result, layoutItem) => {
        result[layoutItem.id] = layoutItem;
        return result;
      }, {});
    },
    dashboardWidgets() {
      const widgets = DASHBOARD_WIDGETS.map((widget) => {
        const layoutItem = this.dashboardLayoutById[widget.id] || {};
        const savedOrder = Number(layoutItem.order);
        // La disponibilidad de cada widget se determina mediante su función isAvailable, que evalúa el contexto de disponibilidad del dashboard,
        // incluyendo los permisos del usuario y otras condiciones relevantes, para decidir si el widget debe estar disponible para su uso en el panel de control.
        const availability = widget.isAvailable(
          this.dashboardAvailabilityContext
        );
        const isForcedSampleRecordsWidget =
          this.shouldForceSampleRecordsWidget(widget);
        const analyticsShortcut = widget.routeName
          ? this.analyticsShortcuts.find(
              (shortcut) => shortcut.routeName === widget.routeName
            )
          : null;
        const unavailableReason = availability.reasonKey
          ? this.$t(availability.reasonKey)
          : availability.reason;

        return {
          ...widget,
          title:
            analyticsShortcut?.title ||
            (widget.titleKey
              ? this.$t(widget.titleKey)
              : widget.title),
          description:
            analyticsShortcut?.description ||
            (widget.descriptionKey
              ? this.$t(widget.descriptionKey)
              : widget.description),
          enabled:
            widget.required || isForcedSampleRecordsWidget
              ? true
              : layoutItem.enabled ?? widget.defaultEnabled,
          required: widget.required || isForcedSampleRecordsWidget,
          order: this.getDashboardWidgetOrder({
            widget,
            savedOrder,
          }),
          orderLocked: Boolean(widget.orderLocked),
          available: availability.available,
          reason: availability.available ? '' : unavailableReason,
          analyticsShortcut: analyticsShortcut || null,
          analyticsShortcuts:
            widget.id === 'powerbi-analytics'
              ? this.analyticsShortcuts
              : [],
        };
      }).sort((firstWidget, secondWidget) => {
        if (firstWidget.order === secondWidget.order) {
          return firstWidget.defaultOrder - secondWidget.defaultOrder;
        }

        return firstWidget.order - secondWidget.order;
      });
      const activeWidgets = this.normalizeVisibleDashboardWidgetRows(
        widgets.filter((widget) => widget.available && widget.enabled)
      );
      const inactiveWidgets = widgets.filter(
        (widget) => !widget.available || !widget.enabled
      );

      return [...activeWidgets, ...inactiveWidgets];
    },
    visibleDashboardWidgets() {
      return this.normalizeVisibleDashboardWidgetRows(
        this.dashboardWidgets.filter(
          (widget) => widget.available && widget.enabled
        )
      );
    },
    dashboardWidgetSizeById() {
      const widgets = this.visibleDashboardWidgets;

      if (widgets.length === 2) {
        return widgets.reduce((result, widget) => {
          result[widget.id] = 'full';
          return result;
        }, {});
      }

      if (this.hasThreeVisibleWidgetsWithTwoRowLocks(widgets)) {
        return this.getThreeWidgetTwoRowLockSizeById(widgets);
      }

      if (this.hasThreeVisibleWidgetsWithSingleRowLock(widgets)) {
        return this.getThreeWidgetSingleRowLockSizeById(widgets);
      }

      return widgets.reduce((result, widget, index) => {
        if (result[widget.id]) {
          return result;
        }

        const row = widgets.slice(index, index + 2);

        if (row.length === 1) {
          result[widget.id] = 'full';
          return result;
        }

        const [firstWidget, secondWidget] = row;

        result[firstWidget.id] = this.getPairedDashboardWidgetSize(
          firstWidget,
          secondWidget
        );
        result[secondWidget.id] = this.getPairedDashboardWidgetSize(
          secondWidget,
          firstWidget
        );

        return result;
      }, {});
    },
    hasVisibleComplaintDashboardWidget() {
      return this.hasVisibleDashboardWidget('complaints');
    },
    hasVisibleSampleRecordsDashboardWidget() {
      return this.hasVisibleDashboardWidget('sample-records');
    },
    hasVisiblePowerBiDashboardWidget() {
      return this.hasVisibleDashboardWidget('powerbi-analytics');
    },
    visibleFlexibleDashboardWidgetIds() {
      return this.visibleDashboardWidgets
        .filter((widget) =>
          FLEXIBLE_DASHBOARD_WIDGET_IDS.includes(widget.id)
        )
        .map((widget) => widget.id);
    },
    configuredVisibleFlexibleDashboardWidgetIds() {
      return DASHBOARD_WIDGETS.filter((widget) =>
        FLEXIBLE_DASHBOARD_WIDGET_IDS.includes(widget.id)
      )
        .filter((widget) => {
          const layoutItem =
            this.dashboardLayoutById[widget.id] || {};
          const availability = widget.isAvailable(
            this.dashboardAvailabilityContext
          );
          const enabled = layoutItem.enabled ?? widget.defaultEnabled;

          return availability.available && enabled;
        })
        .map((widget) => widget.id);
    },
    hasSingleFlexibleWidgetWithFixedWidgets() {
      return false;
    },
    hasSingleFlexibleWidgetWithAnyFixedWidget() {
      return false;
    },
    visibleLockedDashboardWidgetIds() {
      return [];
    },
    singleFlexibleDashboardWidgetId() {
      return null;
    },
    reorderableDashboardWidgetOrderById() {
      const reorderableWidgets = DASHBOARD_WIDGETS.filter(
        (widget) => !this.isDashboardWidgetOrderLocked(widget.id)
      ).sort((firstWidget, secondWidget) => {
        const firstLayoutItem =
          this.dashboardLayoutById[firstWidget.id] || {};
        const secondLayoutItem =
          this.dashboardLayoutById[secondWidget.id] || {};
        const firstOrder = Number.isFinite(
          Number(firstLayoutItem.order)
        )
          ? Number(firstLayoutItem.order)
          : firstWidget.defaultOrder;
        const secondOrder = Number.isFinite(
          Number(secondLayoutItem.order)
        )
          ? Number(secondLayoutItem.order)
          : secondWidget.defaultOrder;

        if (firstOrder === secondOrder) {
          return firstWidget.defaultOrder - secondWidget.defaultOrder;
        }

        return firstOrder - secondOrder;
      });
      const reorderableOrderSlots = DASHBOARD_WIDGETS.filter(
        (widget) => !this.isDashboardWidgetOrderLocked(widget.id)
      )
        .map((widget) => widget.defaultOrder)
        .sort((firstOrder, secondOrder) => firstOrder - secondOrder);

      return reorderableWidgets.reduce((result, widget, index) => {
        result[widget.id] =
          reorderableOrderSlots[index] || widget.defaultOrder;
        return result;
      }, {});
    },
    reorderableDashboardWidgetIds() {
      return this.visibleDashboardWidgets
        .filter((widget) => !widget.orderLocked)
        .map((widget) => widget.id);
    },
    speaksSpanish() {
      return Boolean(this.authenticatedUser);
    },
    showNewsInfoPopUp() {
      // Se muestra el popup si no ha superado el máximo de veces
      // El vídeo de registro de incidencias solo se muestra a los contactos, no a empleados
      return (
        this.hasRemainingShows &&
        (this.currentVideo?.name === 'Registro de muestras' ||
          (this.currentVideo?.name === 'Registro de incidencias' &&
            this.enabled))
      );
    },
    onlineSamplesIconSvg: () => onlineSamplesIconSvg,
    scienceIconSvg: () => scienceIconSvg,
    hasAnySamples() {
      return this.aggregateInfo.some((sample) => {
        // Obtener el primer (y único) valor del objeto
        const sampleData = Object.values(sample)[0];
        return (
          // Las muestras que están "analyzed" no cuentan para el dashboard porque no tienen card
          sampleData.in_process > 0 ||
          sampleData.in_alert > 0 ||
          sampleData.pending_review > 0
        );
      });
    },
    hasTwoVisibleSampleTypes() {
      if (!Array.isArray(this.aggregateInfo)) {
        return false;
      }

      const visibleSampleTypes = this.aggregateInfo.filter(
        (sample) => {
          const sampleData = Object.values(sample || {})[0] || {};
          return (
            Number(sampleData.in_process || 0) > 0 ||
            Number(sampleData.in_alert || 0) > 0 ||
            Number(sampleData.pending_review || 0) > 0
          );
        }
      );

      return visibleSampleTypes.length === 2;
    },
    hasOneVisibleSampleType() {
      if (!Array.isArray(this.aggregateInfo)) {
        return false;
      }

      const visibleSampleTypes = this.aggregateInfo.filter(
        (sample) => {
          const sampleData = Object.values(sample || {})[0] || {};
          return (
            Number(sampleData.in_process || 0) > 0 ||
            Number(sampleData.in_alert || 0) > 0 ||
            Number(sampleData.pending_review || 0) > 0
          );
        }
      );

      return visibleSampleTypes.length === 1;
    },
    shouldJoinAnalyticsShortcuts() {
      return (
        this.hasOneVisibleSampleType || this.hasTwoVisibleSampleTypes
      );
    },
    hasOnlineSamples() {
      return (
        this.onlineSamplesRecord.NumSolicitudesAbiertas > 0 ||
        this.onlineSamplesRecord.NumEnTransito > 0 ||
        this.onlineSamplesRecord.NumRecepcionadas > 0
      );
    },
    hasMaterialRequest() {
      return (
        this.materialRequest.NumEnTransito > 0 ||
        this.materialRequest.NumSolicitudesPendientes > 0 ||
        this.materialRequest.NumEnPreparacion > 0 ||
        this.materialRequest.NumAceptadas > 0 ||
        this.materialRequest.NumRechazadas > 0
      );
    },
    ...mapState('samples', ['aggregateInfo']),
    ...mapState('contact', ['company', 'address']),
    ...mapState('user', ['authenticatedUser']),
    ...mapState('contact', ['enabled']),
    ...mapState('contact', ['hasAccessToAdvanceService']),
    ...mapState('contact', ['hasAccessToNutritionalMonitoring']),
    ...mapState('PowerBiModules', ['availableCountries']),
    ...mapState('dashboardCustomization', {
      dashboardLayout: 'layout',
    }),
    ...mapState('videoPeriodModule', [
      'currentVideo',
      'isRegistroMuestras',
      'isFacturas',
      'isIncidencias',
    ]),
    ...mapGetters('videoPeriodModule', ['hasRemainingShows']),
    ...mapGetters('user', ['getLanguage']),
  },
  watch: {
    loaded(value) {
      if (value) {
        this.scheduleSamplesGuidedTourStart();
        this.scheduleIssuedInvoicesGuidedTourStart();
      }
    },
    currentVideo: {
      handler() {
        this.scheduleSamplesGuidedTourStart();
      },
      deep: true,
    },
    isFacturas(value) {
      if (value) {
        this.scheduleIssuedInvoicesGuidedTourStart();
      }
    },
    authenticatedUser(value) {
      if (value) {
        this.loadDashboardLayoutAction({
          storageContext: this.dashboardStorageContext,
        });
        this.loadVideoPeriodInfo().then(() => {
          this.scheduleSamplesGuidedTourStart();
          this.scheduleIssuedInvoicesGuidedTourStart();
        });
      }
    },
    company: {
      handler() {
        this.loadDashboardLayoutAction({
          storageContext: this.dashboardStorageContext,
        });
        this.reloadData(); // Recargar los datos del dashboard al cambiar de compañía para mostrar la información correspondiente a la nueva compañía seleccionada.
      },
      immediate: false,
    },
    address: {
      handler() {
        this.reloadData();
      },
      immediate: false,
    },
  },
  beforeMount() {
    this.setCurrentRoute([
      {
        name: this.$t('Dashboard'),
        disabled: false,
        page: 'Dashboard',
        active: true,
      },
    ]);
  },
  async mounted() {
    try {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', this.handleWindowResize);
      }
      this.handleWindowResize();
      // this.$f7.preloader.show();
      this.loadDashboardLayoutAction({
        storageContext: this.dashboardStorageContext,
      });
      setTimeout(() => {
        this.applyCurrentFilters();
      }, 0);
      const videoPeriodInfoPromise = this.loadVideoPeriodInfo();
      videoPeriodInfoPromise.then(() => {
        if (this.loaded) {
          this.scheduleSamplesGuidedTourStart();
          this.scheduleIssuedInvoicesGuidedTourStart();
        }
      });
      // Cargar los datos del dashboard después de cargar la configuración del layout para evitar mostrar notificaciones de error de carga de datos antes de que se muestre el dashboard,
      // lo que podría ocurrir si el layout no se ha cargado y por tanto no se han calculado correctamente las propiedades de disponibilidad de los widgets.
      await this.loadDashboardData();

      // Escuchar el evento cuando cambia la compañía
      EventBus.$on('Dashboard', async () => {
        await this.reloadData(); // Recargar los datos del dashboard al recibir el evento para mostrar la información correspondiente a la nueva compañía seleccionada.
      });
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(error.message)
          ),
          width: 350,
        },
        'error',
        3000
      );
    } finally {
      this.loaded = true;
      this.scheduleSamplesGuidedTourStart();
      this.scheduleIssuedInvoicesGuidedTourStart();
      // this.$f7.preloader.hide();
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleWindowResize);
    }
    this.clearSamplesGuidedTourStartTimeout();
    this.clearIssuedInvoicesGuidedTourStartTimeout();
    stopSamplesRegisterGuidedTour(this);
    stopIssuedInvoicesGuidedTour(this);
    EventBus.$off('Dashboard');
  },
  methods: {
    // La función handleWindowResize se encarga de actualizar el estado de la anchura de la ventana cada vez que se redimensiona, lo que permite adaptar el diseño del dashboard a diferentes tamaños de pantalla para mejorar la experiencia del usuario.
    handleWindowResize() {
      this.windowWidth =
        typeof window !== 'undefined' ? window.innerWidth : 1440;
    },
    // La función openCustomizationDrawer se encarga de abrir el cajón de personalización del dashboard, permitiendo al usuario acceder a las opciones para configurar la disposición y visibilidad de los widgets en el panel de control.
    openCustomizationDrawer() {
      this.isCustomizationDrawerOpen = true;
    },
    // La función closeCustomizationDrawer se encarga de cerrar el cajón de personalización del dashboard, permitiendo al usuario salir de las opciones de configuración.
    closeCustomizationDrawer() {
      this.isCustomizationDrawerOpen = false;
      this.hoveredDashboardWidgetId = null;
    },
    handleDashboardWidgetPreviewHover(widgetId) {
      this.hoveredDashboardWidgetId = widgetId;
    },
    handleDashboardWidgetPreviewLeave(widgetId) {
      if (this.hoveredDashboardWidgetId === widgetId) {
        this.hoveredDashboardWidgetId = null;
      }
    },
    // La función handleToggleDashboardWidget se encarga de alternar la visibilidad de un widget específico en el dashboard, actualizando su estado en la configuración del layout del panel de control para reflejar la preferencia del usuario.
    handleToggleDashboardWidget(widgetId) {
      this.toggleDashboardWidgetAction({
        id: widgetId,
        storageContext: this.dashboardStorageContext,
      });
    },
    // Las funciones handleMoveDashboardWidgetUp y handleMoveDashboardWidgetDown se encargan de mover un widget específico hacia arriba o hacia abajo en el orden de los widgets del dashboard, actualizando la configuración del layout del panel de control para reflejar la nueva disposición según la preferencia del usuario.
    handleMoveDashboardWidgetUp(widgetId) {
      if (this.moveFreeDashboardWidgetByVisualSlot(widgetId, -1)) {
        return;
      }

      if (this.hasSingleFlexibleWidgetWithAnyFixedWidget) {
        this.setSingleFlexibleDashboardWidgetOrder(
          widgetId,
          this.getPreviousSingleFlexibleDashboardWidgetOrder(widgetId)
        );
        return;
      }

      this.moveDashboardWidgetUpAction({
        id: widgetId,
        orderedIds: this.reorderableDashboardWidgetIds,
        storageContext: this.dashboardStorageContext,
      });
    },
    handleMoveDashboardWidgetDown(widgetId) {
      if (this.moveFreeDashboardWidgetByVisualSlot(widgetId, 1)) {
        return;
      }

      if (this.hasSingleFlexibleWidgetWithAnyFixedWidget) {
        this.setSingleFlexibleDashboardWidgetOrder(
          widgetId,
          this.getNextSingleFlexibleDashboardWidgetOrder(widgetId)
        );
        return;
      }

      this.moveDashboardWidgetDownAction({
        id: widgetId,
        orderedIds: this.reorderableDashboardWidgetIds,
        storageContext: this.dashboardStorageContext,
      });
    },
    // La función handleRestoreDefaultDashboardLayout se encarga de restaurar el diseño predeterminado del dashboard, actualizando la configuración del layout del panel de control para reflejar la disposición original.
    handleRestoreDefaultDashboardLayout() {
      this.restoreDefaultDashboardLayoutAction({
        storageContext: this.dashboardStorageContext,
      });
    },
    hasVisibleDashboardWidget(widgetId) {
      return this.dashboardWidgets.some(
        (widget) =>
          widget.id === widgetId && widget.available && widget.enabled
      );
    },
    shouldForceSampleRecordsWidget() {
      return false;
    },
    isDashboardWidgetConfiguredVisible(widgetId) {
      const widget = DASHBOARD_WIDGETS.find(
        (dashboardWidget) => dashboardWidget.id === widgetId
      );

      if (!widget) {
        return false;
      }

      const layoutItem = this.dashboardLayoutById[widget.id] || {};
      const availability = widget.isAvailable(
        this.dashboardAvailabilityContext
      );
      const enabled =
        widget.required ||
        (layoutItem.enabled ?? widget.defaultEnabled);

      return availability.available && enabled;
    },
    isDashboardWidgetOrderLocked(widgetId) {
      const widget = DASHBOARD_WIDGETS.find(
        (dashboardWidget) => dashboardWidget.id === widgetId
      );

      return Boolean(widget?.orderLocked);
    },
    getDashboardWidgetOrder({ widget, savedOrder }) {
      return Number.isFinite(savedOrder)
        ? savedOrder
        : widget.defaultOrder;
    },
    isFlexibleWidgetInSecondaryRow(order) {
      return (
        Number(order) >
        this.getDashboardWidgetDefaultOrder('sample-records')
      );
    },
    isFlexibleWidgetLeadingLastSamples(order) {
      return (
        Number.isFinite(Number(order)) &&
        Number(order) <
          this.getDashboardWidgetDefaultOrder('last-samples')
      );
    },
    getSingleFlexibleDashboardWidgetDisplayOrder(savedOrder) {
      return Number(savedOrder);
    },
    getOrderBeforeDashboardWidget(widgetId) {
      return this.getDashboardWidgetDefaultOrder(widgetId);
    },
    getOrderAfterDashboardWidget(widgetId) {
      return this.getDashboardWidgetDefaultOrder(widgetId);
    },
    getDashboardWidgetDefaultOrder(widgetId) {
      const widget = DASHBOARD_WIDGETS.find(
        (dashboardWidget) => dashboardWidget.id === widgetId
      );

      return widget ? widget.defaultOrder : Number.MAX_SAFE_INTEGER;
    },
    setSingleFlexibleDashboardWidgetOrder(widgetId, order) {
      if (
        !this.configuredVisibleFlexibleDashboardWidgetIds.includes(
          widgetId
        )
      ) {
        return;
      }

      this.setDashboardWidgetOrderAction({
        id: widgetId,
        order,
        storageContext: this.dashboardStorageContext,
      });
    },
    getSingleFlexibleDashboardWidgetOrder(widgetId) {
      const widget = this.visibleDashboardWidgets.find(
        (visibleWidget) => visibleWidget.id === widgetId
      );

      return Number(widget?.order ?? 0);
    },
    getPreviousSingleFlexibleDashboardWidgetOrder(widgetId) {
      const currentOrder =
        this.getSingleFlexibleDashboardWidgetOrder(widgetId);

      if (!this.hasSingleFlexibleWidgetWithFixedWidgets) {
        const fixedWidgetId = this.visibleLockedDashboardWidgetIds[0];

        return this.getOrderBeforeDashboardWidget(fixedWidgetId);
      }

      return Math.max(1, currentOrder - 1);
    },
    getNextSingleFlexibleDashboardWidgetOrder(widgetId) {
      const currentOrder =
        this.getSingleFlexibleDashboardWidgetOrder(widgetId);

      if (!this.hasSingleFlexibleWidgetWithFixedWidgets) {
        const fixedWidgetId = this.visibleLockedDashboardWidgetIds[0];

        return this.getOrderAfterDashboardWidget(fixedWidgetId);
      }

      return currentOrder + 1;
    },
    getRowLockedDashboardWidgets(widgets) {
      return widgets.filter((widget) =>
        this.isRowLockedDashboardWidget(widget)
      );
    },
    hasThreeVisibleWidgetsWithSingleRowLock(widgets) {
      return (
        widgets.length === 3 &&
        this.getRowLockedDashboardWidgets(widgets).length === 1
      );
    },
    hasThreeVisibleWidgetsWithTwoRowLocks(widgets) {
      return (
        widgets.length === 3 &&
        this.getRowLockedDashboardWidgets(widgets).length === 2
      );
    },
    getThreeWidgetTwoRowLockWidgets(widgets) {
      return {
        firstRowLockedWidget: widgets.find(
          (widget) => widget.rowLock === 'first'
        ),
        secondRowLockedWidget: widgets.find(
          (widget) => widget.rowLock === 'second'
        ),
        freeWidget: widgets.find(
          (widget) => !this.isRowLockedDashboardWidget(widget)
        ),
      };
    },
    getThreeWidgetTwoRowLockFreeSlot(widgets) {
      const { freeWidget } =
        this.getThreeWidgetTwoRowLockWidgets(widgets);
      const order = Number(freeWidget?.order);

      if (!Number.isFinite(order)) {
        return Number(freeWidget?.visualOrder) || 2;
      }

      return Math.min(Math.max(Math.round(order), 1), 4);
    },
    getThreeWidgetTwoRowLockVisualWidgets(widgets) {
      const {
        firstRowLockedWidget,
        secondRowLockedWidget,
        freeWidget,
      } = this.getThreeWidgetTwoRowLockWidgets(widgets);

      if (
        !firstRowLockedWidget ||
        !secondRowLockedWidget ||
        !freeWidget
      ) {
        return [];
      }

      const freeSlot = this.getThreeWidgetTwoRowLockFreeSlot(widgets);

      if (freeSlot === 1) {
        return [
          freeWidget,
          firstRowLockedWidget,
          secondRowLockedWidget,
        ];
      }

      if (freeSlot === 4) {
        return [
          firstRowLockedWidget,
          secondRowLockedWidget,
          freeWidget,
        ];
      }

      return [
        firstRowLockedWidget,
        freeWidget,
        secondRowLockedWidget,
      ];
    },
    moveThreeWidgetTwoRowLockFreeWidget(widgetId, direction) {
      const widgets = this.visibleDashboardWidgets;

      if (!this.hasThreeVisibleWidgetsWithTwoRowLocks(widgets)) {
        return false;
      }

      const { freeWidget } =
        this.getThreeWidgetTwoRowLockWidgets(widgets);

      if (!freeWidget || freeWidget.id !== widgetId) {
        return false;
      }

      const currentSlot =
        this.getThreeWidgetTwoRowLockFreeSlot(widgets);
      const nextSlot = Math.min(
        Math.max(currentSlot + direction, 1),
        4
      );

      if (nextSlot === currentSlot) {
        return true;
      }

      this.setDashboardWidgetOrderAction({
        id: widgetId,
        order: nextSlot,
        storageContext: this.dashboardStorageContext,
      });

      return true;
    },
    moveFreeDashboardWidgetByVisualSlot(widgetId, direction) {
      const widgets = this.visibleDashboardWidgets;
      const widget = widgets.find(
        (visibleWidget) => visibleWidget.id === widgetId
      );

      if (
        !widget ||
        this.isRowLockedDashboardWidget(widget) ||
        !this.getRowLockedDashboardWidgets(widgets).length
      ) {
        return false;
      }

      const currentSlot = this.hasThreeVisibleWidgetsWithTwoRowLocks(
        widgets
      )
        ? this.getThreeWidgetTwoRowLockFreeSlot(widgets)
        : widgets.findIndex(
            (visibleWidget) => visibleWidget.id === widgetId
          ) + 1;
      const maxSlot = this.hasThreeVisibleWidgetsWithTwoRowLocks(
        widgets
      )
        ? 4
        : widgets.length;
      const nextSlot = Math.min(
        Math.max(currentSlot + direction, 1),
        maxSlot
      );

      if (nextSlot === currentSlot) {
        return true;
      }

      this.setDashboardWidgetOrderAction({
        id: widgetId,
        order: nextSlot,
        storageContext: this.dashboardStorageContext,
      });

      return true;
    },
    getAllowedVisibleSlotsForRowLock(rowLock, widgetsCount) {
      if (widgetsCount <= 2) {
        return [1, 2].slice(0, widgetsCount);
      }

      if (rowLock === 'first') {
        return widgetsCount >= 2 ? [1, 2] : [1];
      }

      if (rowLock === 'second') {
        return widgetsCount === 3 ? [2, 3] : [3, 4];
      }

      return [];
    },
    normalizeVisibleDashboardWidgetRows(widgets = []) {
      if (!widgets.length) {
        return [];
      }

      if (this.hasThreeVisibleWidgetsWithTwoRowLocks(widgets)) {
        const visualWidgets =
          this.getThreeWidgetTwoRowLockVisualWidgets(widgets);

        if (visualWidgets.length) {
          return visualWidgets.map((widget, index) => ({
            ...widget,
            visualOrder: index + 1,
          }));
        }
      }

      const normalizedSlots = [];
      const usedSlots = new Set();

      this.getRowLockedDashboardWidgets(widgets).forEach((widget) => {
        const allowedSlots = this.getAllowedVisibleSlotsForRowLock(
          widget.rowLock,
          widgets.length
        );
        const currentSlot =
          widgets.findIndex(
            (visibleWidget) => visibleWidget.id === widget.id
          ) + 1;
        const isCurrentSlotAllowed =
          allowedSlots.includes(currentSlot) &&
          !usedSlots.has(currentSlot);
        const targetSlot = isCurrentSlotAllowed
          ? currentSlot
          : allowedSlots.find((slot) => !usedSlots.has(slot));

        if (!targetSlot) {
          return;
        }

        normalizedSlots[targetSlot - 1] = widget;
        usedSlots.add(targetSlot);
      });

      widgets
        .filter((widget) => !this.isRowLockedDashboardWidget(widget))
        .forEach((widget) => {
          const nextSlot = widgets.findIndex(
            (unusedWidget, index) =>
              unusedWidget &&
              !normalizedSlots[index] &&
              !usedSlots.has(index + 1)
          );

          if (nextSlot === -1) {
            normalizedSlots.push(widget);
            return;
          }

          normalizedSlots[nextSlot] = widget;
          usedSlots.add(nextSlot + 1);
        });

      return normalizedSlots.filter(Boolean).map((widget, index) => ({
        ...widget,
        visualOrder: index + 1,
      }));
    },
    isRowLockedDashboardWidget(widget) {
      return Boolean(widget?.rowLock);
    },
    getDashboardWidgetSizeForSpan(span) {
      const sizesBySpan = {
        3: 'side',
        4: 'analytics',
        5: 'compact',
        6: 'medium',
        7: 'large',
        8: 'lead',
        9: 'wide',
        12: 'full',
      };

      return sizesBySpan[span] || 'medium';
    },
    getPairedDashboardWidgetSize(widget, pairedWidget) {
      if (this.isRowLockedDashboardWidget(widget)) {
        return widget.size;
      }

      if (this.isRowLockedDashboardWidget(pairedWidget)) {
        const pairedWidgetSpan = this.getDashboardWidgetSpan(
          pairedWidget.size
        );

        return this.getDashboardWidgetSizeForSpan(
          12 - pairedWidgetSpan
        );
      }

      return this.getDashboardWidgetBaseSize(widget);
    },
    getThreeWidgetSingleRowLockSizeById(widgets) {
      const [lockedWidget] =
        this.getRowLockedDashboardWidgets(widgets);
      const result = {};
      const pairedRow =
        lockedWidget.rowLock === 'second'
          ? widgets.slice(1, 3)
          : widgets.slice(0, 2);
      const fullWidget =
        lockedWidget.rowLock === 'second' ? widgets[0] : widgets[2];

      if (fullWidget) {
        result[fullWidget.id] = 'full';
      }

      if (pairedRow.length === 2) {
        const [firstWidget, secondWidget] = pairedRow;

        result[firstWidget.id] = this.getPairedDashboardWidgetSize(
          firstWidget,
          secondWidget
        );
        result[secondWidget.id] = this.getPairedDashboardWidgetSize(
          secondWidget,
          firstWidget
        );
      }

      return result;
    },
    getThreeWidgetTwoRowLockSizeById(widgets) {
      const {
        firstRowLockedWidget,
        secondRowLockedWidget,
        freeWidget,
      } = this.getThreeWidgetTwoRowLockWidgets(widgets);

      if (
        !firstRowLockedWidget ||
        !secondRowLockedWidget ||
        !freeWidget
      ) {
        return {};
      }

      const freeSlot = this.getThreeWidgetTwoRowLockFreeSlot(widgets);

      if (freeSlot <= 2) {
        return {
          [firstRowLockedWidget.id]: firstRowLockedWidget.size,
          [freeWidget.id]: this.getPairedDashboardWidgetSize(
            freeWidget,
            firstRowLockedWidget
          ),
          [secondRowLockedWidget.id]: 'full',
        };
      }

      return {
        [firstRowLockedWidget.id]: 'full',
        [freeWidget.id]: this.getPairedDashboardWidgetSize(
          freeWidget,
          secondRowLockedWidget
        ),
        [secondRowLockedWidget.id]: secondRowLockedWidget.size,
      };
    },
    getFlexibleDashboardWidgetSize(widgetId) {
      if (this.hasSingleFlexibleWidgetWithFixedWidgets) {
        return 'analytics';
      }

      const widgetIndex =
        this.visibleFlexibleDashboardWidgetIds.indexOf(widgetId);

      if (
        this.hasVisibleDashboardWidget('last-samples') &&
        (widgetIndex === 0 ||
          (widgetId === 'powerbi-analytics' &&
            this.shouldPairPowerBiWithSampleRecords()))
      ) {
        return 'analytics';
      }

      if (!this.hasVisibleSampleRecordsDashboardWidget) {
        return 'full';
      }

      return widgetId === 'complaints' ? 'compact' : 'medium';
    },
    getDashboardWidgetBaseSize(widget) {
      if (widget.id === 'last-samples') {
        return widget.size;
      }

      if (widget.id === 'powerbi-analytics') {
        return this.getFlexibleDashboardWidgetSize(widget.id);
      }

      if (widget.id === 'sample-records') {
        return widget.size;
      }

      if (widget.id === 'complaints') {
        return this.getFlexibleDashboardWidgetSize(widget.id);
      }

      return widget.size;
    },
    shouldPairPowerBiWithSampleRecords() {
      const powerBiIndex =
        this.visibleFlexibleDashboardWidgetIds.indexOf(
          'powerbi-analytics'
        );

      return (
        this.hasVisibleSampleRecordsDashboardWidget &&
        powerBiIndex > 0
      );
    },
    isSingleFlexibleWidgetInSecondaryRow() {
      if (!this.singleFlexibleDashboardWidgetId) {
        return false;
      }

      const layoutItem =
        this.dashboardLayoutById[
          this.singleFlexibleDashboardWidgetId
        ] || {};

      return this.isFlexibleWidgetInSecondaryRow(layoutItem.order);
    },
    getDashboardWidgetSpan(size) {
      const spansBySize = {
        wide: 9,
        lead: 8,
        analytics: 4,
        side: 3,
        large: 7,
        compact: 5,
        medium: 6,
        full: 12,
      };

      return spansBySize[size] || 6;
    },
    // El tamaño final de cada widget se determina combinando su tamaño base, que depende de su tipo y de la presencia de otros widgets específicos en el panel de control,
    // con posibles ajustes adicionales definidos en la configuración del layout personalizado del usuario, para adaptar la apariencia del dashboard a las preferencias individuales sin perder coherencia visual.
    getDashboardWidgetSize(widget) {
      return (
        this.dashboardWidgetSizeById[widget.id] ||
        this.getDashboardWidgetBaseSize(widget)
      );
    },
    navigateToShortcut(routeName) {
      this.$f7router.navigate(
        { name: routeName },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    getVideoPeriodLanguage() {
      return (
        this.getLanguage?.toLowerCase() ||
        this.$i18n?.locale?.toLowerCase() ||
        this.authenticatedUser?.language_code?.toLowerCase() ||
        'es'
      );
    },
    async loadVideoPeriodInfo() {
      if (!this.authenticatedUser) return;

      try {
        await this.fetchUrlPeriodVideos(
          this.getVideoPeriodLanguage()
        );
      } catch (error) {
        // No bloquea el dashboard si falla la configuración del tour.
      }
    },
    clearSamplesGuidedTourStartTimeout() {
      if (this.guidedTourStartTimeoutId) {
        clearTimeout(this.guidedTourStartTimeoutId);
        this.guidedTourStartTimeoutId = null;
      }
    },
    clearIssuedInvoicesGuidedTourStartTimeout() {
      if (this.issuedInvoicesGuidedTourStartTimeoutId) {
        clearTimeout(this.issuedInvoicesGuidedTourStartTimeoutId);
        this.issuedInvoicesGuidedTourStartTimeoutId = null;
      }
    },
    scheduleSamplesGuidedTourStart({ force = false } = {}) {
      if (!this.loaded) return;
      if (!force && this.hasTriedAutoStartSamplesGuidedTour) return;

      if (!force && !this.isTemplatesVideoPeriodActive) {
        return;
      }

      if (
        !shouldAutoStartSamplesRegisterTemplatesGuidedTour(
          this.$store,
          {
            force,
          }
        )
      ) {
        return;
      }

      if (!force) {
        this.hasTriedAutoStartSamplesGuidedTour = true;
      }

      this.clearSamplesGuidedTourStartTimeout();
      this.guidedTourStartTimeoutId = setTimeout(() => {
        this.guidedTourStartTimeoutId = null;
        startSamplesRegisterTemplatesDashboardIntroTour(this, {
          force,
        });
      }, SAMPLES_REGISTER_GUIDED_TOUR_AUTO_START_DELAY);
    },
    scheduleIssuedInvoicesGuidedTourStart({ force = false } = {}) {
      if (!this.loaded) return;
      if (!force && this.hasTriedAutoStartIssuedInvoicesGuidedTour) {
        return;
      }

      if (!force && !this.isFacturas) {
        return;
      }

      if (!force && this.isRegistroMuestras) {
        return;
      }

      if (
        !shouldAutoStartIssuedInvoicesGuidedTour(this.$store, {
          force,
        })
      ) {
        return;
      }

      if (!force) {
        this.hasTriedAutoStartIssuedInvoicesGuidedTour = true;
      }

      this.clearIssuedInvoicesGuidedTourStartTimeout();
      this.issuedInvoicesGuidedTourStartTimeoutId = setTimeout(() => {
        this.issuedInvoicesGuidedTourStartTimeoutId = null;
        startIssuedInvoicesDashboardIntroTour(this, {
          force,
        });
      }, ISSUED_INVOICES_GUIDED_TOUR_AUTO_START_DELAY);
    },
    // La función loadDashboardData se encarga de cargar los datos necesarios para mostrar el panel de control, incluyendo la información agregada de muestras y las quejas, y maneja el estado de carga y los posibles errores durante este proceso.
    async loadDashboardData() {
      this.loaded = false;

      try {
        const [response] = await Promise.all([
          this.getAggregateInfo(),
          this.loadComplaintWidgetData(),
        ]);
        this.processAggregateResponse(response);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 350,
          },
          'error',
          3000
        );
      } finally {
        this.lastDashboardRefreshAt = new Date();
        this.loaded = true;
      }
    },
    loadComplaintWidgetData() {
      if (!this.canShowComplaintWidget) {
        return this.setComplaintDashboard(null);
      }

      return this.fetchComplaintDashboard().catch(() => {
        // El widget ya pinta su propio estado de error.
      });
    },
    processAggregateResponse(response) {
      // Procesar datos de albaranes (registro de muestras)
      if (response?.albaranes_data) {
        this.onlineSamplesRecord.NumSolicitudesAbiertas =
          response.albaranes_data.NumSolicitudesAbiertas || 0;
        this.onlineSamplesRecord.NumEnTransito =
          response.albaranes_data.NumEnTransito || 0;
        this.onlineSamplesRecord.NumRecepcionadas =
          response.albaranes_data.NumRecepcionadas || 0;
      }
      // Procesar datos de solicitud de materiales
      if (response?.material_data) {
        this.materialRequest.NumEnTransito =
          response.material_data.NumEnTransito || 0;
        this.materialRequest.NumSolicitudesPendientes =
          response.material_data.NumSolicitudesPendientes || 0;
        this.materialRequest.NumEnPreparacion =
          response.material_data.NumEnPreparacion || 0;
        this.materialRequest.NumAceptadas =
          response.material_data.NumAceptadas || 0;
        this.materialRequest.NumRechazadas =
          response.material_data.NumRechazadas || 0;
      }
    },
    // La función reloadData se utiliza para recargar los datos del panel de control,
    // generalmente en respuesta a cambios en la compañía o la dirección, y sigue un proceso similar a loadDashboardData pero con un manejo específico del estado de carga para mostrar un indicador de carga durante el proceso.
    async reloadData() {
      try {
        this.loaded = false;
        setTimeout(() => {
          this.applyCurrentFilters();
        }, 0);
        const [response] = await Promise.all([
          this.getAggregateInfo(),
          this.loadComplaintWidgetData(),
        ]);
        this.processAggregateResponse(response);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 350,
          },
          'error',
          3000
        );
      } finally {
        this.lastDashboardRefreshAt = new Date();
        this.loaded = true;
      }
    },
    ...mapActions('filter', [
      'setCurrentFilter',
      'applyCurrentFilters',
    ]),
    ...mapActions('samples', ['getAggregateInfo']),
    ...mapActions('complaintManagement', [
      'fetchComplaintDashboard',
      'setComplaintDashboard',
    ]),
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('videoPeriodModule', ['fetchUrlPeriodVideos']),
    ...mapActions('dashboardCustomization', {
      loadDashboardLayoutAction: 'loadDashboardLayout',
      toggleDashboardWidgetAction: 'toggleDashboardWidget',
      setDashboardWidgetOrderAction: 'setDashboardWidgetOrder',
      moveDashboardWidgetUpAction: 'moveDashboardWidgetUp',
      moveDashboardWidgetDownAction: 'moveDashboardWidgetDown',
      restoreDefaultDashboardLayoutAction:
        'restoreDefaultDashboardLayout',
    }),
  },
};
</script>

<style lang="scss">
#main-dashboard,
#main-dashboard-samples {
  --dashboard-feature-card-min-height: 210px;
  --dashboard-feature-card-half-min-height: calc(
    (var(--dashboard-feature-card-min-height) - 16px) / 2
  );
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;

  .dashboard-toolbar {
    position: fixed;
    right: 28px;
    bottom: 28px;
    z-index: 1000;
    width: auto;
    margin: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .dashboard-toolbar__customize-button {
    position: relative;
    width: 53px;
    height: 53px;
    border: 1px solid var(--Neutral-20, #e8e7ea);
    border-radius: 8px;
    background: var(--Basic-white, #fff);
    color: var(--Primary-Blue-70, #00346a);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    box-shadow: 0 1px 4px 0 rgba(87, 84, 95, 0.18);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    i {
      font-size: 27px;
    }
  }

  .dashboard-toolbar__customize-button--with-tooltip::before,
  .dashboard-toolbar__customize-button--with-tooltip::after {
    position: absolute;
    top: 50%;
    left: -10px;
    pointer-events: none;
    opacity: 0;
    transform: translate(-10px, -50%) scale(0.96);
    transition: opacity 0.22s ease, transform 0.22s ease;
  }

  .dashboard-toolbar__customize-button--with-tooltip::before {
    content: attr(data-tooltip);
    z-index: 2;
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1px;
    color: #f6fbff;
    white-space: nowrap;
    background: rgba(12, 23, 37, 0.86);
    border: 1px solid rgba(255, 255, 255, 0.18);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(7px);
    -webkit-backdrop-filter: blur(7px);
  }

  .dashboard-toolbar__customize-button--with-tooltip::after {
    content: '';
    z-index: 1;
    width: 9px;
    height: 9px;
    margin-left: -5px;
    background: rgba(12, 23, 37, 0.86);
    border-right: 1px solid rgba(255, 255, 255, 0.18);
    border-bottom: 1px solid rgba(255, 255, 255, 0.18);
    transform: translate(-10px, -50%) rotate(-45deg);
    transition: opacity 0.22s ease, transform 0.22s ease;
  }

  .dashboard-toolbar__customize-button--with-tooltip:hover::before,
  .dashboard-toolbar__customize-button--with-tooltip:hover::after,
  .dashboard-toolbar__customize-button--with-tooltip:focus-visible::before,
  .dashboard-toolbar__customize-button--with-tooltip:focus-visible::after {
    opacity: 1;
    transform: translate(-100%, -50%) scale(1);
  }

  .dashboard-toolbar__customize-button--with-tooltip:hover::after,
  .dashboard-toolbar__customize-button--with-tooltip:focus-visible::after {
    transform: translate(-6px, -50%) rotate(-45deg);
  }

  .dashboard-toolbar__customize-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px 0 rgba(4, 80, 159, 0.1);
  }

  &#main-dashboard-samples.dashboard-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    grid-auto-flow: row;
    gap: 24px;
    align-items: stretch;
    width: calc(100% - 6px);
    margin: 0 3px;
  }

  &#main-dashboard-samples.dashboard-grid--empty {
    min-height: calc(100vh - 170px);
    align-content: center;
  }

  .dashboard-empty-widgets-state {
    grid-column: 1 / -1;
    width: 100%;
    max-width: 1000px;
    min-height: 280px;
    justify-self: center;
    padding: 34px 28px;
    border: 1px solid var(--Neutral-20, #e8e7ea);
    border-radius: 10px;
    background: var(--Basic-white, #fff);
    box-shadow: 0px 1px 4px 0px rgba(87, 84, 95, 0.3);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 18px;
    text-align: center;
    box-sizing: border-box;
  }

  .dashboard-empty-widgets-icon {
    width: 58px;
    height: 58px;
    border-radius: 16px;
    background: var(--Primary-Blue-10, #e8f3ff);
    color: var(--Primary-Blue-70, #00346a);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 26px;
    }
  }

  .dashboard-empty-widgets-copy {
    max-width: 430px;

    h2 {
      margin: 0 0 8px;
      color: var(--Primary-Blue-60, #04509f);
      font-size: 20px;
      font-weight: 600;
      line-height: 1.25;
    }

    p {
      margin: 0;
      color: var(--Neutral-70, #3e3c44);
      font-size: 15px;
      font-weight: 400;
      line-height: 1.5;
    }
  }

  .dashboard-grid__item {
    min-width: 0;
    position: relative;
    transition: transform 0.2s ease, filter 0.2s ease;
    will-change: transform;
  }

  .dashboard-grid__item--order-hover {
    z-index: 1;
    transform: translateY(-4px);
    filter: drop-shadow(0 14px 5px rgba(25, 127, 235, 0.184));
  }

  .dashboard-grid__item--wide {
    grid-column: span 9;
  }

  .dashboard-grid__item--lead {
    grid-column: span 8;
  }

  .dashboard-grid__item--analytics {
    grid-column: span 4;
  }

  .dashboard-grid__item--side {
    grid-column: span 3;
  }

  .dashboard-grid__item--large {
    grid-column: span 7;
  }

  .dashboard-grid__item--compact {
    grid-column: span 5;
  }

  .dashboard-grid__item--medium {
    grid-column: span 6;
  }

  .dashboard-grid__item--full {
    grid-column: span 12;
  }

  @media (max-width: 1600px) and (min-width: 1181px) {
    .dashboard-grid__item--wide {
      grid-column: span 8;
    }

    .dashboard-grid__item--side {
      grid-column: span 4;
    }
  }

  @media (max-width: 1180px) {
    &#main-dashboard-samples.dashboard-grid {
      grid-template-columns: 1fr;
      width: 100%;
      margin: 0;
    }

    .dashboard-grid__item--wide,
    .dashboard-grid__item--lead,
    .dashboard-grid__item--analytics,
    .dashboard-grid__item--side,
    .dashboard-grid__item--large,
    .dashboard-grid__item--compact,
    .dashboard-grid__item--medium,
    .dashboard-grid__item--full {
      grid-column: span 1;
    }
  }

  @media (max-width: 768px) {
    .dashboard-toolbar {
      right: 18px;
      bottom: 18px;
    }
  }

  .top-section {
    width: calc(100% - 6px);
    max-width: none;
    margin: 0 3px;
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(260px, 1fr);
    gap: 24px;
    align-items: stretch;
  }

  .dashboard-top-samples {
    min-width: 0;
    display: flex;
  }

  .dashboard-top-samples > * {
    width: 100%;
    margin: 0;
  }

  .two-columns {
    display: flex;
    gap: 24px;
  }

  // Por ahora se comenta el card de solicitud de materiales, y se adapta para que ambos cards ocupen un 80% centrados y uno sobre otro
  .column {
    flex: none;
    width: calc(100% - 6px);
    max-width: none;
    margin: 0 3px;
  }

  .dashboard-content-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
    align-items: stretch;
    max-width: 100%;
  }

  .dashboard-content-row.has-complaint-chart {
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, 1fr);
  }

  .dashboard-content-row.has-two-shortcuts {
    // Con dos shortcuts visibles, la fila se compone como un mosaico
    // de tres columnas donde el bloque de registros necesita más aire.
    grid-template-columns: minmax(0, 2.4fr) minmax(260px, 0.8fr) minmax(
        260px,
        0.8fr
      );
  }

  .dashboard-content-row.has-one-shortcut {
    grid-template-columns: minmax(0, 2fr) minmax(290px, 0.6fr);
  }

  .dashboard-content-row.has-two-shortcuts.is-compact {
    grid-template-columns: minmax(0, 1fr);
    gap: 20px;
  }

  .dashboard-content-row.has-no-shortcuts {
    grid-template-columns: minmax(0, 1fr);
  }

  .dashboard-main-card,
  .dashboard-shortcuts,
  .dashboard-complaint-chart-slot {
    min-width: 0;
  }

  .dashboard-main-card {
    display: flex;
    align-self: stretch;
    width: 100%;
  }

  .dashboard-main-card > * {
    width: 100%;
  }

  .dashboard-shortcuts {
    display: grid;
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: 16px;
    height: 100%;
    align-self: stretch;
  }

  .dashboard-shortcuts.joined-shortcuts {
    gap: 0;
    border-radius: 10px;
    background: var(--Basic-white, #fff);
    box-shadow: 0px 1px 4px 0px rgba(87, 84, 95, 0.3);
    overflow: hidden;
  }

  .dashboard-shortcuts.joined-shortcuts .dashboard-shortcut-card {
    border-radius: 0;
    box-shadow: none;
  }

  .dashboard-shortcuts.joined-shortcuts
    .dashboard-shortcut-card
    + .dashboard-shortcut-card {
    border-top: 1px solid rgba(190, 187, 195, 0.45);
  }

  .dashboard-shortcuts.joined-shortcuts
    .dashboard-shortcut-card:hover {
    transform: none;
    box-shadow: none;
    background: var(--Neutral-10, #f7f7f8);
  }

  .dashboard-shortcuts.joined-shortcuts .dashboard-shortcut-arrow {
    display: none;
  }

  .dashboard-shortcuts.compact-shortcuts {
    grid-template-rows: repeat(2, auto);
    height: auto;
    align-self: start;
  }

  .dashboard-shortcuts.compact-shortcuts .dashboard-shortcut-card {
    min-height: 0;
    height: auto;
    padding: 14px 16px;
  }

  .dashboard-shortcuts.compact-shortcuts .dashboard-shortcut-content {
    .dashboard-shortcut-heading {
      margin-bottom: 8px;
    }

    p {
      font-size: 13px;
      line-height: 1.35;
    }
  }

  .dashboard-complaint-chart-slot {
    display: flex;
    min-height: var(--dashboard-feature-card-min-height);
  }

  .dashboard-complaint-record {
    width: 100%;
  }

  .top-section .dashboard-shortcut-card {
    min-height: 0;
    padding: 17px 18px 14px;
  }

  .top-section .dashboard-shortcut-content {
    .dashboard-shortcut-heading {
      gap: 12px;
      margin-bottom: 10px;
    }

    h2 {
      font-size: 16px;
    }

    p {
      font-size: 13px;
      line-height: 1.4;
    }
  }

  .top-section .dashboard-shortcut-icon {
    width: 46px;
    height: 46px;
  }

  .dashboard-content-row.has-two-shortcuts .dashboard-main-card {
    min-width: 0;
    height: 100%;
  }

  .dashboard-content-row.has-two-shortcuts
    #main-online-samples-record {
    // En el layout de tres columnas apilamos cabecera y CTA para que los
    // tres estados puedan vivir en vertical sin quedarse sin ancho útil.
    .dashboard-bottom-col-title {
      flex-direction: column;
      align-items: stretch;
      gap: 14px;
    }

    .dashboard-bottom-col-header-button {
      width: 100%;

      button {
        width: 100%;
      }
    }
  }

  .dashboard-content-row.has-two-shortcuts.has-samples
    #main-online-samples-record {
    // Si sí hay contadores, recuperamos la cabecera horizontal para no
    // robar altura al bloque principal ni sobredimensionar el CTA.
    .dashboard-bottom-col-title {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }

    .dashboard-bottom-col-header-button {
      width: auto;

      button {
        width: auto;
        min-width: 175px;
      }
    }
  }

  .dashboard-content-row.has-two-shortcuts.is-compact.has-samples
    #main-online-samples-record {
    .dashboard-bottom-col-title {
      flex-direction: row;
      align-items: center;
      gap: 16px;
    }
  }

  .dashboard-content-row.has-two-shortcuts .dashboard-shortcuts {
    // Los shortcuts se "sueltan" del wrapper para que ocupen la segunda
    // y tercera columna reales de la misma grid que usa el dashboard.
    display: contents;
  }

  .dashboard-content-row.has-two-shortcuts.is-compact
    .dashboard-shortcuts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: none;
    gap: 20px;
  }

  .dashboard-content-row.has-two-shortcuts .dashboard-shortcut-card {
    min-height: 100%;
    padding: 18px 22px 14px;
  }

  .dashboard-content-row.has-two-shortcuts.is-compact
    .dashboard-shortcut-card {
    padding: 18px 16px 14px;
  }

  .dashboard-content-row.has-two-shortcuts
    .dashboard-shortcut-content {
    justify-content: center;

    .dashboard-shortcut-heading {
      gap: 16px;
      margin-bottom: 12px;
    }

    p {
      font-size: 14px;
      line-height: 1.45;
    }

    h2 {
      font-size: 17px;
    }
  }

  .dashboard-content-row.has-two-shortcuts.is-compact
    .dashboard-shortcut-content {
    .dashboard-shortcut-heading {
      gap: 12px;
      margin-bottom: 10px;
      align-items: flex-start;
    }

    h2 {
      font-size: 16px;
      line-height: 1.3;
    }

    p {
      font-size: 13px;
      line-height: 1.45;
    }
  }

  .dashboard-content-row.has-two-shortcuts .dashboard-shortcut-label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .dashboard-content-row.has-two-shortcuts .dashboard-shortcut-icon {
    width: 50px;
    height: 50px;
    border-radius: 14px;
    gap: 5px;
    padding: 9px;

    span {
      width: 7px;
    }

    span:nth-child(1) {
      height: 16px;
    }

    span:nth-child(2) {
      height: 24px;
    }

    span:nth-child(3) {
      height: 32px;
    }
  }

  .dashboard-content-row.has-two-shortcuts.is-compact
    .dashboard-shortcut-icon {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    padding: 8px;

    span {
      width: 6px;
    }

    span:nth-child(1) {
      height: 14px;
    }

    span:nth-child(2) {
      height: 22px;
    }

    span:nth-child(3) {
      height: 28px;
    }
  }

  .dashboard-shortcuts.single-shortcut.with-empty-state
    .dashboard-shortcut-card {
    // Cuando no hay registros y solo hay un tablero, el shortcut rellena
    // más volumen visual para equilibrar la ausencia de contadores.
    min-height: 210px;
    padding: 24px 26px 18px;
  }

  .dashboard-shortcuts.single-shortcut.with-empty-state
    .dashboard-shortcut-content {
    justify-content: center;
    max-width: 450px;
    margin: 0 auto;
    // text-align: center;

    .dashboard-shortcut-heading {
      gap: 16px;
      margin-bottom: 16px;
      justify-content: center;
    }

    h2 {
      font-size: 18px;
    }

    p {
      font-size: 15px;
      line-height: 1.55;
    }
  }

  .dashboard-shortcuts.single-shortcut.with-empty-state
    .dashboard-shortcut-label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .dashboard-shortcuts.single-shortcut.with-empty-state
    .dashboard-shortcut-title-group {
    width: 100%;
  }

  .dashboard-shortcuts.single-shortcut.with-empty-state
    .dashboard-shortcut-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    gap: 6px;
    padding: 10px;

    span {
      width: 8px;
    }

    span:nth-child(1) {
      height: 18px;
    }

    span:nth-child(2) {
      height: 28px;
    }

    span:nth-child(3) {
      height: 36px;
    }
  }

  .dashboard-shortcuts.single-shortcut {
    grid-template-rows: minmax(0, 1fr);
  }

  .dashboard-content-row.has-one-shortcut.has-samples
    .dashboard-shortcut-card {
    // Con un único shortcut y contadores visibles, lo hacemos más cuadrado
    // para que no quede una columna muy alta y visualmente vacía.
    min-height: 210px;
    padding: 24px 28px 18px;
  }

  .dashboard-content-row.has-one-shortcut.has-samples
    .dashboard-shortcut-content {
    max-width: 320px;
    margin: 0 auto;
    text-align: center;

    .dashboard-shortcut-heading {
      justify-content: center;
      gap: 16px;
      margin-bottom: 16px;
    }

    h2 {
      font-size: 18px;
    }

    p {
      font-size: 15px;
      line-height: 1.55;
    }
  }

  .dashboard-content-row.has-one-shortcut.has-samples
    .dashboard-shortcut-title-group {
    width: 100%;
  }

  .dashboard-content-row.has-one-shortcut.has-samples
    .dashboard-shortcut-icon {
    width: 54px;
    height: 54px;
    border-radius: 16px;
    gap: 6px;
    padding: 10px;

    span {
      width: 8px;
    }

    span:nth-child(1) {
      height: 18px;
    }

    span:nth-child(2) {
      height: 28px;
    }

    span:nth-child(3) {
      height: 36px;
    }
  }

  .dashboard-shortcuts.no-shortcuts {
    display: none;
  }

  .dashboard-shortcut-card {
    // Igualamos la base visual con las cards de contadores: fondo sólido,
    // sombra definida y recorte real del radio en las esquinas.
    border: none;
    box-shadow: 0px 1px 4px 0px rgba(87, 84, 95, 0.3);
    background: var(--Basic-white, #fff);
    border-radius: 10px;
    overflow: hidden;
    padding: 20px 20px 18px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    width: 100%;
    min-height: 190px;
    cursor: pointer;
    transition: transform 0.2s ease, box-shadow 0.2s ease,
      border-color 0.2s ease;
  }

  .dashboard-shortcuts.two-shortcuts .dashboard-shortcut-card {
    // Con dos accesos rápidos, cada card usa la mitad de la altura base
    // para que ambos llenen la columna sin dejar hueco ni desbordar.
    min-height: var(--dashboard-feature-card-half-min-height);
    height: auto;
  }

  .dashboard-shortcuts.two-shortcuts.compact-shortcuts
    .dashboard-shortcut-card {
    min-height: 0;
  }

  .dashboard-shortcut-card.full-height {
    min-height: unquote(
      'max(100%, var(--dashboard-feature-card-min-height))'
    );
    height: 100%;
  }

  .dashboard-shortcut-card:hover {
    transform: translateY(-2px);
    box-shadow: 0px 8px 18px 0px rgba(4, 80, 159, 0.1);
    border-color: var(--Neutral-30, #bebbc3);
  }

  .dashboard-shortcut-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 5px;
    padding: 9px;
    box-sizing: border-box;

    span {
      display: block;
      width: 7px;
      border-radius: 999px;
    }

    span:nth-child(1) {
      height: 15px;
    }

    span:nth-child(2) {
      height: 24px;
    }

    span:nth-child(3) {
      height: 32px;
    }
  }

  .dashboard-shortcut-icon.is-green {
    background: rgba(220, 237, 182, 0.35);

    span {
      background: linear-gradient(
        180deg,
        var(--Secondary-green-60, #72a300) 0%,
        #476600 100%
      );
    }
  }

  .dashboard-shortcut-icon.is-yellow {
    background: rgba(255, 208, 51, 0.2);

    span {
      background: linear-gradient(180deg, #ffd84d 0%, #d68000 100%);
    }
  }

  .dashboard-shortcut-content {
    flex: 1;
    min-width: 0;
    text-align: left;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    .dashboard-shortcut-heading {
      display: flex;
      align-items: center;
      gap: 14px;
      margin-bottom: 14px;
    }

    .dashboard-shortcut-title-group {
      min-width: 0;
    }

    h2 {
      margin: 0;
    }

    p {
      margin: 0;
      color: var(--Neutral-70, #3e3c44);
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .dashboard-shortcut-label {
    display: inline-block;
    margin-bottom: 4px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--Neutral-60, #6a6872);
  }

  .dashboard-shortcuts.single-shortcut .dashboard-shortcut-card {
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 10px;
    padding: 20px 20px 18px;
    min-height: 190px;
  }

  .dashboard-shortcuts.single-shortcut .dashboard-shortcut-content {
    width: 100%;

    p {
      max-width: none;
    }
  }

  .dashboard-shortcuts.single-shortcut .dashboard-shortcut-arrow {
    align-self: flex-end;
    margin-left: 0;
  }

  @media (max-width: 1100px) {
    .top-section {
      grid-template-columns: 1fr;
    }

    .dashboard-content-row {
      grid-template-columns: 1fr;
    }

    .dashboard-content-row.has-complaint-chart {
      grid-template-columns: 1fr;
    }

    .dashboard-shortcuts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-template-rows: 1fr;
    }

    .dashboard-shortcuts.joined-shortcuts
      .dashboard-shortcut-card
      + .dashboard-shortcut-card {
      border-top: 0;
      border-left: 1px solid rgba(190, 187, 195, 0.45);
    }

    .dashboard-content-row.has-two-shortcuts .dashboard-shortcuts {
      display: grid;
    }

    .dashboard-shortcuts.single-shortcut {
      grid-template-columns: 1fr;
      grid-template-rows: minmax(0, 1fr);
    }

    .dashboard-shortcuts.single-shortcut .dashboard-shortcut-card {
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      gap: 10px;
      padding: 18px 18px 16px;
    }

    .dashboard-shortcuts.single-shortcut .dashboard-shortcut-arrow {
      align-self: flex-end;
      margin-left: 0;
    }
  }

  @media (max-width: 768px) {
    .two-columns {
      flex-direction: column;
    }

    .top-section {
      width: 100%;
      max-width: 100%;
      margin: 0;
    }

    .column {
      width: 100%;
      max-width: 100%;
      margin: 0;
    }

    .dashboard-shortcuts {
      grid-template-columns: 1fr;
    }

    .dashboard-shortcuts.joined-shortcuts
      .dashboard-shortcut-card
      + .dashboard-shortcut-card {
      border-top: 1px solid rgba(190, 187, 195, 0.45);
      border-left: 0;
    }

    .dashboard-shortcut-card {
      padding: 16px 14px 15px;
      gap: 12px;
      min-height: 165px;
    }

    .dashboard-shortcut-icon {
      width: 44px;
      height: 44px;
      border-radius: 12px;
    }

    .dashboard-shortcut-content {
      .dashboard-shortcut-heading {
        gap: 10px;
        margin-bottom: 8px;
      }

      p {
        font-size: 13px;
      }
    }
  }

  h2 {
    font-weight: 500;
    font-size: 17px;
    line-height: 125%;
    color: var(--Primary-Blue-60, #04509f);
    margin: 0;
  }

  /* Tarjetas de registro y solicitudes de materiales */
  #main-online-samples-record,
  #main-material-request-card {
    box-shadow: 0px 1px 4px 0px #57545f4d;
    background: white;
    border-radius: 10px;
    padding: 20px 28px;
    gap: 28px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    height: 100%;

    .dashboard-bottom-col-title {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .dashboard-bottom-col-header-name {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-left:5px
      }

      .dashboard-bottom-col-header-button {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
        button {
          // El botón de registrar muestra comparte el mismo patrón "flotante"
          // que el resto de tarjetas accionables del dashboard.
          width: 175px;
          height: 40px;
          border-radius: 10px;
          gap: 8px;
          background: var(--Primary-Blue-70, #00346a);
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          line-height: 20px;
          border: none;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease,
            background-color 0.2s ease;
        }
        button:hover {
          background: var(--Primary-Blue-60, #04509f);
          transform: translateY(-2px);
          box-shadow: 0px 8px 18px 0px rgba(4, 80, 159, 0.12);
        }
      }
    }

    .dashboard-bottom-col-content {
      display: flex;
      flex-direction: column;
      gap: 20px;
      /* Tamaño de iconos svg */
      img.status-icon {
        width: 18.75px;
        height: 18.75px;
        display: block;
        object-fit: contain;
      }
    }

    .dashboard-bottom-col-content-item {
      border-radius: 10px;
      gap: 8px;
      opacity: 1;
      padding-top: 6px;
      padding-right: 8px;
      padding-bottom: 6px;
      padding-left: 16px;
      background: var(--Neutral-10, #f7f7f8);
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.is-clickable {
        border: 1px solid transparent;
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease,
          background-color 0.2s ease;
      }

      &.is-clickable:hover {
        transform: translateY(-1px);
        box-shadow: 0px 8px 18px 0px rgba(4, 80, 159, 0.12);
        border: 1px solid rgba(156, 155, 158, 0.15);
        // background-color: #ffffff;
      }

      .dashboard-bottom-col-content-item-title {
        display: flex;
        align-items: center;
        gap: 8px;
        width: auto;

        .dashboard-bottom-col-content-item-text {
          width: 100%;
          display: flex;
          align-content: flex-start;
          flex-wrap: wrap;
          flex-direction: column;
          p {
            margin: 0;
            padding: 0;
            font-weight: 600;
            font-style: Semi Bold;
            font-size: 15px;
            line-height: 140%;
            letter-spacing: 0%;
            color: var(--Primary-Blue-60, #04509f);
          }
          p.detail {
            font-size: 14px;
          }
        }
        /* Textos con colores especiales*/
        .dashboard-bottom-col-content-item-text.content-text-warning {
          p {
            color: var(--Warning-60, #c95c04);
          }
        }
        .dashboard-bottom-col-content-item-text.content-text-success {
          p {
            color: var(--Success-70, #008043);
          }
        }
        .dashboard-bottom-col-content-item-text.content-text-neutral {
          p {
            color: var(--Neutral-70, #3e3c44);
          }
        }
      }

      .dashboard-bottom-col-content-item-number {
        width: 20%;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        p {
          margin: 0;
          padding: 0 16px;
          font-weight: 400;
          font-style: Regular;
          font-size: 15px;
          line-height: 140%;
          letter-spacing: 0%;
          text-decoration: underline;
          text-decoration-style: solid;
          text-decoration-thickness: 0%;
          color: var(--Neutral-70, #3e3c44);
        }
      }
    }
  }

  #main-online-samples-record {
    .dashboard-bottom-col-content {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    &.layout-stacked {
      .dashboard-bottom-col-header-button {
        button {
          width: 126px;
          min-width: 0;
          height: 40px;
          padding: 0 12px;
          font-size: 11px;
          line-height: 14px;
          white-space: nowrap;
        }
      }
    }
  }

  .main-no-samples-card .sample-button-action {
    // Repetimos el comportamiento del CTA anterior para que el estado vacío
    // y el estado con datos se perciban como el mismo componente.
    transition: transform 0.2s ease, box-shadow 0.2s ease,
      background-color 0.2s ease;
  }

  .main-no-samples-card .sample-button-action:hover {
    background: var(--Primary-Blue-60, #04509f);
    transform: translateY(-2px);
    box-shadow: 0px 8px 18px 0px rgba(4, 80, 159, 0.12);
  }

  @media (max-width: 1100px) {
    #main-online-samples-record {
      .dashboard-bottom-col-content {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }

  @media (max-width: 768px) {
    #main-online-samples-record {
      .dashboard-bottom-col-content {
        grid-template-columns: 1fr;
      }
    }
  }
}

@media (min-width: 1601px) {
  #main-dashboard,
  #main-dashboard-samples {
    --dashboard-feature-card-min-height: 250px;
  }
}

@media (max-width: 1600px) and (min-width: 1024px) {
  #main-dashboard,
  #main-dashboard-samples {
    .top-section .dashboard-shortcut-arrow {
      display: none;
    }

    .top-section
      .dashboard-shortcuts.compact-shortcuts
      .dashboard-shortcut-card {
      padding: 12px 14px;
    }

    .dashboard-shortcuts.compact-shortcuts .dashboard-shortcut-icon {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      gap: 4px;
      padding: 8px;

      span {
        width: 6px;
      }

      span:nth-child(1) {
        height: 13px;
      }

      span:nth-child(2) {
        height: 20px;
      }

      span:nth-child(3) {
        height: 27px;
      }
    }

    .dashboard-shortcuts.compact-shortcuts .dashboard-shortcut-label {
      margin-bottom: 2px;
      font-size: 11px;
    }

    .dashboard-shortcuts.compact-shortcuts
      .dashboard-shortcut-content {
      .dashboard-shortcut-heading {
        gap: 10px;
        margin-bottom: 4px;
      }

      h2 {
        font-size: 15px;
        line-height: 1.15;
      }

      p {
        display: -webkit-box;
        overflow: hidden;
        font-size: 12px;
        line-height: 1.25;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
        line-clamp: 1;
      }
    }

    #main-online-samples-record {
      .dashboard-bottom-col-title {
        .dashboard-bottom-col-header-button {
          margin-right: 0px;
        }
      }
    }
  }
}
</style>
